import { useCallback, useEffect, useState } from 'react';
import { WorkTabKey, WorkScope } from './WorkTabs';
import { tasksService, Task as ApiTask } from '../../../services/tasksService';
import sopInstanceService from '../../../services/sopInstanceService';
import statutoryService from '../../../services/statutoryService';
import { litInstanceService } from '../../../services/litInstanceService';
import { controlTestProgramService } from '../../../services/controlTestingService';
import { itaProgramService } from '../../../services/itaService';
import { communicationsService } from '../../../services/auditService';
import { riskAssessmentProgramService } from '../../../services/riskAssessmentProgramService';
import { sopProgramService } from '../../../services/sopProgramService';
import { renewalsReviewsService, RrKind } from '../../../services/renewalsReviewsService';
import { capaInvestigationsService } from '../../../services/capaInvestigationsService';
import { governanceMeetingsService } from '../../../services/governanceService';
import { pendingApprovalsCount } from '../../../services/hrService';
import pfmService from '../../../services/pfmService';
import { projectService } from '../../../services/projectService';
import {
  isClosedInstance, isClosedProgram, isClosedLitigation, isClosedMeeting, isClosedProject,
} from './workArchive';

export type WorkCounts = Partial<Record<WorkTabKey, number>>;

// Mirror of WorkView.isCompletedForUser — a task is "done for me" when it has
// reached a terminal state. VERIFIED = "Pending Ratification" — all roles
// (doer, approver, validator) count it as active until it becomes DONE.
const isCompletedForUser = (t: ApiTask): boolean => {
  if (['DONE', 'ARCHIVED', 'CANCELLED', 'REJECTED'].includes(t.status)) return true;
  return false;
};

// Maker + checker lists overlap; count distinct rows by id.
const dedupeRows = (...lists: Array<Array<{ id: number }>>): Array<{ id: number; status?: string | null }> => {
  const map = new Map<number, any>();
  for (const list of lists) for (const r of list) map.set(r.id, r);
  return Array.from(map.values());
};

/**
 * Loads the count for EVERY rail tab in one parallel burst so the badges render
 * together. `scope` flips each query between pending (Action Centre) and
 * closed/archived (History); the closed-record predicates live in workArchive.ts
 * so counts always match what each tab body renders. A failed query leaves that
 * tab's badge blank. Reloads when scope changes.
 */
export function useWorkCounts(scope: WorkScope = 'active') {
  const [counts, setCounts] = useState<WorkCounts>({});
  const [loading, setLoading] = useState(true);
  const archive = scope === 'archive';
  // Active = the full pending list each tab already shows (unchanged Action Centre
  // numbers). Archive = only the closed/completed rows that tab will now show.
  const count = <T,>(rows: T[], closed: (r: T) => boolean): number =>
    (archive ? rows.filter(closed) : rows).length;

  const reload = useCallback(async () => {
    setLoading(true);
    const next: WorkCounts = {};

    const set = (key: WorkTabKey, p: Promise<number>): Promise<void> =>
      p.then((n) => { next[key] = n; }).catch(() => { /* leave blank */ });

    const jobs: Promise<void>[] = [];

    // tasks + audit share one /tasks fetch. Active = pending; archive = completed.
    jobs.push((async () => {
      try {
        const ts = await tasksService.list({ mine: true, limit: 200 });
        const inScope = ts.filter((t) => (archive ? isCompletedForUser(t) : !isCompletedForUser(t) && (t as any).my_role !== 'CREATOR'));
        next.tasks = inScope.length;
        const auditTasks = inScope.filter((t) => (t as any).source_type_code === 'AUDIT_QUERY').length;
        // The Audit tab body also surfaces external communications addressed to me
        // (QueriesModule recipientScope). Count the open ones (active) / closed ones
        // (archive) so the rail badge matches what the tab actually shows.
        let auditComms = 0;
        try {
          const r = await communicationsService.list({
            page: 1, limit: 1, channel: 'external', recipient_only: true,
            status: archive ? 'closed' : 'open',
          });
          auditComms = r.total;
        } catch { /* leave comms at 0 */ }
        next.audit = auditTasks + auditComms;
      } catch { /* leave blank */ }
    })());

    // renewals + reviews share one fetch. Active = non-completed buckets; archive = completed.
    jobs.push((async () => {
      try {
        const items = await renewalsReviewsService.mine();
        const cnt = (k: RrKind) =>
          items.filter((i) => i.kind === k && (archive ? i.bucket === 'completed' : i.bucket !== 'completed')).length;
        next.renewals = cnt('renewal');
        next.reviews = cnt('review');
      } catch { /* leave blank */ }
    })());

    jobs.push(set('compliance', sopInstanceService.listInstances('my_tasks').then((r) => count(r, (x: any) => isClosedInstance(x.status)))));
    jobs.push(set('statutory', statutoryService.listInstances('my_tasks').then((r) => count(r, (x: any) => isClosedInstance(x.status)))));
    jobs.push(set('litigation', litInstanceService.getMine().then((r) => count(r, (x: any) => isClosedLitigation(x.status)))));
    jobs.push(set('control-testing', controlTestProgramService.list({ mine: true }).then((r) => count(r, (x: any) => isClosedProgram(x.status)))));
    jobs.push(set('it-assessment', itaProgramService.list({ mine: true }).then((r) => count(r, (x: any) => isClosedProgram(x.status)))));

    // capa — archive uses the dedicated CLOSED query
    jobs.push(set('capa', (archive
      ? capaInvestigationsService.list({ mine: true, inv_status: 'CLOSED' })
      : capaInvestigationsService.myInvestigations()
    ).then((r) => r.length)));

    jobs.push(set('meetings', governanceMeetingsService.getMine(false).then((r) => count(r, (m: any) => isClosedMeeting(m)))));
    jobs.push(set('leave-approvals', archive ? Promise.resolve(0) : pendingApprovalsCount()));
    // Pending performance forms for this user (PFM-COLL-1).
    jobs.push(set('performance-measures', archive ? Promise.resolve(0) : pfmService.pendingCount()));

    // My Projects — led-by-me or I'm an active resource. Active = open projects;
    // archive = closed (COMPLETED/CANCELLED) projects.
    jobs.push(set('projects', projectService.getMyProjects(1, 100).then((r) => count(r.data, (p: any) => isClosedProject(p.status)))));

    // risk-assessments — maker + checker merged
    jobs.push((async () => {
      try {
        const [a, b] = await Promise.all([
          riskAssessmentProgramService.list({ assigned_to_me: true }),
          riskAssessmentProgramService.list({ checker_for_me: true }),
        ]);
        next['risk-assessments'] = count(dedupeRows(a, b), (x) => isClosedProgram(x.status));
      } catch { /* leave blank */ }
    })());

    // process-walkthroughs — maker + checker merged, per variant
    const pwt = async (variant: 'Design' | 'Compliance', key: WorkTabKey) => {
      try {
        const [a, b] = await Promise.all([
          sopProgramService.list({ assigned_to_me: true, variant }),
          sopProgramService.list({ checker_for_me: true, variant }),
        ]);
        next[key] = count(dedupeRows(a, b), (x) => isClosedProgram(x.status));
      } catch { /* leave blank */ }
    };
    jobs.push(pwt('Design', 'process-walkthroughs-design'));
    jobs.push(pwt('Compliance', 'process-walkthroughs-compliance'));

    await Promise.allSettled(jobs);
    setCounts(next);
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope]);

  useEffect(() => { reload(); }, [reload]);

  const total = Object.values(counts).reduce((a, b) => a + (b ?? 0), 0);

  return { counts, total, loading, reload };
}
